import {
	type ColumnDef,
	useReactTable,
	getCoreRowModel,
	getPaginationRowModel,
	getSortedRowModel,
	getFilteredRowModel,
	type VisibilityState,
	type ColumnFiltersState,
} from "@tanstack/react-table";
import {
	DropdownMenu,
	DropdownMenuTrigger,
	DropdownMenuContent,
} from "../../../components/ui/dropdown-menu";
import { Button } from "../../../components/ui/button";
import { format } from "date-fns";
import {
	Sheet,
	SheetContent,
	SheetHeader,
	SheetTitle,
	SheetTrigger,
} from "../../../components/ui/sheet";
import FilterIcon from "../../../assets/svgs/common/filter.svg";
import { Separator } from "../../../components/ui/separator";
import type { DateRange } from "react-day-picker";
import { Calendar } from "../../../components/ui/calendar";
import { CalendarIcon, XIcon } from "lucide-react";
import React, { useEffect } from "react";
import { useState } from "react";
import Spinner from "../../../components/spinner.components";
import { DataTableComponents } from "../../../components/tables/table.components";
import type { IPagination } from "../../../types/models/pagination.models.types";
import { useGetPayoutTransactionsAdminHttp } from "../../../hooks/http/admin/payouts/get-payout-transcations.admin.http";
import usePayoutsStore from "../../../store/payouts.store";
import { useToast } from "../../../hooks/use-toast";
import type { IPayoutTransactionMinimal } from "../../../types/models/payout-transaction.modals.types";
import { handleHttpError } from "../../../utils/utils";
import { DateTime } from "luxon";
import { ApproveButton } from "../../../components/action-buttons/payouts/approve-button.component";
import { RejectButton } from "../../../components/action-buttons/payouts/reject-button.component";

export function SelectDateRangeFilterModal({
	fromDate,
	toDate,
	onSelect,
}: {
	fromDate: Date | null;
	toDate: Date | null;
	onSelect: (from: Date | null, to: Date | null) => void;
}) {
	const [open, setOpen] = useState(false);
	const [range, setRange] = useState<DateRange | undefined>(
		fromDate || toDate
			? { from: fromDate ?? undefined, to: toDate ?? undefined }
			: undefined,
	);

	function handleApply() {
		onSelect(range?.from ?? null, range?.to ?? null);
		setOpen(false);
	}

	function handleClear() {
		setRange(undefined);
		onSelect(null, null);
		setOpen(false);
	}

	return (
		<Sheet open={open} onOpenChange={setOpen}>
			<SheetTrigger asChild>
				<div className="gap-2 p-3 border-border w-full cursor-pointer">
					Filter by Date Range
				</div>
			</SheetTrigger>
			<SheetContent className="p-0 border-border border-l w-full max-w-sm">
				<SheetHeader>
					<SheetTitle className="flex justify-between items-center p-5 pr-16 font-medium text-white">
						Select Date Range
						{(fromDate || toDate) && (
							<Button
								onClick={handleClear}
								className="bg-rejectbuttonbg border border-rejectbuttonborder text-rejectbuttontext"
							>
								Clear Filter
							</Button>
						)}
					</SheetTitle>
				</SheetHeader>
				<Separator />
				<div className="flex flex-col h-[calc(100vh-80px)]">
					<div className="flex flex-col gap-4 p-5 items-center justify-center">
						{range?.from || range?.to ? (
							<p className="flex items-center gap-2 text-sm text-secondary">
								<CalendarIcon className="w-4 h-4" />
								{range.from ? format(range.from, "dd-MM-yyyy") : "…"}
								{" → "}
								{range.to ? format(range.to, "dd-MM-yyyy") : "…"}
							</p>
						) : (
							<p className="flex items-center gap-2 text-sm text-secondary">
								<CalendarIcon className="w-4 h-4" />
								No range selected
							</p>
						)}
						<Calendar
							mode="range"
							selected={range}
							onSelect={setRange}
							toDate={new Date()}
							numberOfMonths={1}
							className=" bg-background border-border rounded-md scale-125 mt-10"
						/>
					</div>
					<div className="p-3 mt-auto">
						<Button
							onClick={handleApply}
							className="w-full bg-primarybuttonbg text-black"
							disabled={!range?.from && !range?.to}
						>
							Apply
						</Button>
					</div>
				</div>
			</SheetContent>
		</Sheet>
	);
}

const PayoutRequetsAdminPartial: React.FunctionComponent = () => {
	const [columnFilters, setColumnFilters] = React.useState<ColumnFiltersState>(
		[],
	);
	const [columnVisibility, setColumnVisibility] =
		React.useState<VisibilityState>({});
	const [rowSelection, setRowSelection] = React.useState({});
	const { data, setData, removeById } = usePayoutsStore();
	const [, setPageNo] = React.useState(1);
	const { toast } = useToast();
	const [paginationData, setPaginationData] = React.useState<IPagination>();
	const [filters, setFilters] = useState<{
		fromDate: Date | null;
		toDate: Date | null;
	}>({
		fromDate: null,
		toDate: null,
	});
	const { execute: getPayoutsHttp, fetching } =
		useGetPayoutTransactionsAdminHttp();

	async function getPayouts(data: IPayoutTransactionMinimal[], pageNo: number) {
		const queryParams: Record<string, string | number | boolean> = {
			page: pageNo,
			transactionStatus: "pendingApproval",
		};
		if (filters.fromDate) {
			queryParams.fromDate = filters.fromDate.toISOString();
		}
		if (filters.toDate) {
			queryParams.toDate = filters.toDate.toISOString();
		}
		const response = await getPayoutsHttp({
			body: null,
			queryParams,
		});
		if (!response.success) {
			return handleHttpError(response, toast);
		}
		setData([...data, ...response.data.transactions]);
		setPaginationData(response.data.pagination);
	}

	// biome-ignore lint/correctness/useExhaustiveDependencies: <explanation>
	useEffect(() => {
		getPayouts([], 1);
	}, []);

	// biome-ignore lint/correctness/useExhaustiveDependencies: <explanation>
	useEffect(() => {
		setData([]);
		setPageNo(1);
		getPayouts([], 1);
	}, [filters.fromDate, filters.toDate]);

	const columns: ColumnDef<IPayoutTransactionMinimal>[] = [
		{
			accessorKey: "creator",
			header: "Creator name",
			cell: ({ row }) => {
				const { creator } = row.original;

				return (
					<div className="font-semibold tracking-wider">
						<p>{creator.name}</p>
						<p>{creator.phoneNumber}</p>
					</div>
				);
			},
		},
		{
			accessorKey: "amount",
			header: "Amount",
			cell: ({ row }) => {
				const { totalAmount } = row.original;

				return (
					<div className="font-semibold tracking-wider">₹ {totalAmount}</div>
				);
			},
		},
		{
			accessorKey: "creator",
			header: "Wallet balance",
			cell: ({ row }) => {
				const { creator } = row.original;

				return (
					<div className="font-semibold tracking-wider">
						₹ {creator.walletBalance}
					</div>
				);
			},
		},
		{
			accessorKey: "createdAt",
			header: "Date",
			cell: ({ row }) => {
				const { createdAt } = row.original;

				return (
					<div className="font-semibold tracking-wider">
						{DateTime.fromISO(new Date(createdAt).toISOString()).toFormat(
							"dd-MM-yyyy hh:mm a",
						)}
					</div>
				);
			},
		},

		{
			accessorKey: "id",
			header: "",
			cell: ({ row }) => {
				return (
					<div className="flex justify-end items-center gap-4">
						<ApproveButton payout={row.original} removeById={removeById} />
						<RejectButton payout={row.original} />
					</div>
				);
			},
		},
	];

	const table = useReactTable({
		data,
		columns,
		onColumnFiltersChange: setColumnFilters,
		getCoreRowModel: getCoreRowModel(),
		getPaginationRowModel: getPaginationRowModel(),
		getSortedRowModel: getSortedRowModel(),
		getFilteredRowModel: getFilteredRowModel(),
		onColumnVisibilityChange: setColumnVisibility,
		onRowSelectionChange: setRowSelection,
		state: {
			columnFilters,
			columnVisibility,
			rowSelection,
		},
		manualPagination: true,
	});

	return (
		<div className="flex flex-col gap-16 px-10 pt-16">
			<div className="flex justify-between items-center">
				<h1 className="font-semibold text-2xl">Payout requests</h1>
				<DropdownMenu>
					<DropdownMenuTrigger>
						<Button className="flex bg-tertiarybuttonbg px-8 py-7 border border-border rounded-xl font-semibold">
							<FilterIcon /> Filters
						</Button>
					</DropdownMenuTrigger>
					<DropdownMenuContent
						className="flex flex-col p-0 divide-y divide-border"
						align="end"
					>
						<SelectDateRangeFilterModal
							fromDate={filters.fromDate}
							toDate={filters.toDate}
							onSelect={(from, to) =>
								setFilters((prev) => ({ ...prev, fromDate: from, toDate: to }))
							}
						/>
					</DropdownMenuContent>
				</DropdownMenu>
			</div>
			{(filters.fromDate || filters.toDate) && (
				<div className="flex flex-wrap gap-2 mb-4">
					{(filters.fromDate || filters.toDate) && (
						<div className="flex items-center gap-2 bg-tertiarybuttonbg px-4 py-2 border border-border rounded-lg">
							<CalendarIcon className="w-4 h-4" />
							<span className="font-medium text-sm">
								{filters.fromDate ? format(filters.fromDate, "dd-MM-yyyy") : "…"}
								{" → "}
								{filters.toDate ? format(filters.toDate, "dd-MM-yyyy") : "…"}
							</span>
							<XIcon
								className="w-4 h-4 cursor-pointer"
								onClick={() => setFilters((prev) => ({ ...prev, fromDate: null, toDate: null }))}
							/>
						</div>
					)}
					<Button
						onClick={() => {
							setFilters({ fromDate: null, toDate: null });
						}}
						className="bg-rejectbuttonbg border border-rejectbuttonborder text-rejectbuttontext"
					>
						Clear All Filters
					</Button>
				</div>
			)}
			{data.length <= 0 && fetching ? (
				<div className="flex justify-center items-center pt-20 w-full">
					<Spinner />
				</div>
			) : (
				<DataTableComponents
					table={table as ReturnType<typeof useReactTable>}
					columns={columns as ColumnDef<IPayoutTransactionMinimal>[]}
					setPageNo={() => {
						setPageNo((prev) => {
							const nextPage = prev + 1;
							getPayouts(data, nextPage);
							return nextPage;
						});
					}}
					loading={fetching}
					tableType="payout"
					paginationData={paginationData}
				/>
			)}
		</div>
	);
};

export default PayoutRequetsAdminPartial;